/**
 * scrollStore.js
 *
 * A module-level store for scroll + pointer state.
 *
 * Why not React state: the 3D scene samples this every frame (60–120×/sec).
 * Routing that through useState would re-render the entire tree on every
 * scroll tick. Instead the driver writes into a plain mutable object, the
 * WebGL layer reads it inside useFrame, and only the handful of DOM widgets
 * that genuinely need to re-render (nav highlight, progress %) subscribe.
 */

export const scrollState = {
  /** 0 → 1 across the whole document */
  progress: 0,
  /** smoothed progress — what the camera actually follows */
  smooth: 0,
  /** signed scroll delta, decays to 0; drives the "warp" effect */
  velocity: 0,
  /** raw pixel offset */
  y: 0,
  /** index of the section currently occupying the viewport centre */
  section: 0,
  /** -1 → 1 normalised pointer, smoothed */
  pointer: { x: 0, y: 0 },
  pointerTarget: { x: 0, y: 0 },
  /** viewport */
  vw: typeof window !== "undefined" ? window.innerWidth : 1280,
  vh: typeof window !== "undefined" ? window.innerHeight : 800,
};

/* ---------------- quality tiering ---------------- */

export function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Three tiers, decided once at boot. `low` is not a fallback for failure —
 * it's the correct target for a mid-range phone, which is most of the
 * traffic this site will actually get.
 */
export function getQualityTier() {
  if (typeof window === "undefined") return "low";
  if (prefersReducedMotion()) return "low";

  const w = window.innerWidth;
  const cores = navigator.hardwareConcurrency || 4;
  const mem = navigator.deviceMemory || 4;
  const coarse =
    window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

  if (w < 768 || cores <= 4 || mem <= 2) return "low";
  if (coarse || cores <= 6 || mem <= 4 || w < 1280) return "mid";
  return "high";
}

export const QUALITY = {
  low: { particles: 900, dpr: [1, 1.25], rings: 2, segments: 24, blur: false },
  mid: { particles: 2200, dpr: [1, 1.5], rings: 3, segments: 48, blur: true },
  high: { particles: 4200, dpr: [1, 1.75], rings: 4, segments: 96, blur: true },
};

/* ---------------- subscribers (coarse, throttled) ---------------- */

const listeners = new Set();

/**
 * Subscribe to coarse scroll updates. The callback receives
 * `{ progress, section }` and only fires when one of them changes
 * meaningfully — not on every frame.
 */
export function subscribeScroll(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

let lastEmittedProgress = -1;
let lastEmittedSection = -1;

function emit() {
  const p = Math.round(scrollState.progress * 200) / 200; // 0.5% steps
  if (p === lastEmittedProgress && scrollState.section === lastEmittedSection) {
    return;
  }
  lastEmittedProgress = p;
  lastEmittedSection = scrollState.section;
  listeners.forEach((fn) => fn(scrollState));
}

/* ---------------- the driver ---------------- */

let running = false;
let rafId = null;
let sectionEls = [];

function measureSections(ids) {
  sectionEls = ids
    .map((id) => document.getElementById(id))
    .filter(Boolean);
}

/**
 * Starts the single rAF loop that owns all scroll-derived state.
 * Returns a teardown function.
 */
export function startScrollDriver(sectionIds = []) {
  if (typeof window === "undefined" || running) return () => {};
  running = true;

  let lastY = window.scrollY;

  const onResize = () => {
    scrollState.vw = window.innerWidth;
    scrollState.vh = window.innerHeight;
    measureSections(sectionIds);
  };

  const onPointerMove = (e) => {
    scrollState.pointerTarget.x = (e.clientX / scrollState.vw) * 2 - 1;
    scrollState.pointerTarget.y = -((e.clientY / scrollState.vh) * 2 - 1);
  };

  // Device orientation gives mobile the same parallax that mouse gives desktop.
  const onOrient = (e) => {
    if (e.gamma == null || e.beta == null) return;
    scrollState.pointerTarget.x = Math.max(-1, Math.min(1, e.gamma / 45));
    scrollState.pointerTarget.y = Math.max(-1, Math.min(1, (e.beta - 45) / 45));
  };

  const tick = () => {
    const y = window.scrollY;
    const max = Math.max(
      1,
      document.documentElement.scrollHeight - window.innerHeight
    );

    scrollState.y = y;
    scrollState.progress = Math.min(1, Math.max(0, y / max));

    // exponential smoothing — the camera never snaps
    scrollState.smooth += (scrollState.progress - scrollState.smooth) * 0.08;

    // velocity decays so the warp effect settles when scrolling stops
    const delta = y - lastY;
    lastY = y;
    scrollState.velocity += (delta - scrollState.velocity) * 0.15;
    if (Math.abs(scrollState.velocity) < 0.01) scrollState.velocity = 0;

    scrollState.pointer.x +=
      (scrollState.pointerTarget.x - scrollState.pointer.x) * 0.06;
    scrollState.pointer.y +=
      (scrollState.pointerTarget.y - scrollState.pointer.y) * 0.06;

    // which section owns the viewport centre
    const centre = y + scrollState.vh * 0.45;
    let idx = 0;
    for (let i = 0; i < sectionEls.length; i++) {
      if (sectionEls[i].offsetTop <= centre) idx = i;
    }
    scrollState.section = idx;

    emit();
    rafId = requestAnimationFrame(tick);
  };

  onResize();
  // Sections mount lazily (Suspense), so re-measure shortly after boot.
  const remeasure = setTimeout(onResize, 1200);
  const remeasure2 = setTimeout(onResize, 3000);

  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("pointermove", onPointerMove, { passive: true });
  window.addEventListener("deviceorientation", onOrient, { passive: true });
  rafId = requestAnimationFrame(tick);

  return () => {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    clearTimeout(remeasure);
    clearTimeout(remeasure2);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("deviceorientation", onOrient);
  };
}
